import { ChangeEvent } from 'react';

// third-party
import { useTranslation } from 'react-i18next';

// material-ui
import { Theme } from '@mui/material/styles';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project-imports
import { HEADER_HEIGHT } from 'config';
import MainCard from 'components/MainCard';
import useConfig from 'hooks/useConfig';
import { FONT_SIZE_SCALE } from 'themes/typography';

// types
import { FontSize } from 'types/config';

// ==============================|| CUSTOMIZATION - FONT SIZE ||============================== //

export default function ThemeFontSize() {
  const { t } = useTranslation();
  const {
    state: { fontSize },
    setField
  } = useConfig();

  const currentFontSize = fontSize ?? 'default';

  const handleFontSizeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setField('fontSize', event.target.value as FontSize);
  };

  const sizes: { id: FontSize; value: FontSize; label: string }[] = [
    {
      id: 'small',
      value: 'small',
      label: t('settingsPane.fontSize.small')
    },
    {
      id: 'default',
      value: 'default',
      label: t('settingsPane.fontSize.default')
    },
    {
      id: 'large',
      value: 'large',
      label: t('settingsPane.fontSize.large')
    },
    {
      id: 'extra-large',
      value: 'extra-large',
      label: t('settingsPane.fontSize.extraLarge')
    },
    {
      id: 'xx-large',
      value: 'xx-large',
      label: t('settingsPane.fontSize.xxLarge')
    }
  ];

  return (
    <RadioGroup row aria-label="font-size" name="font-size" value={currentFontSize} onChange={handleFontSizeChange}>
      <Grid container spacing={1.75} sx={{ ml: 0 }}>
        {sizes.map((item, index) => (
          <Grid key={index}>
            <FormControlLabel
              control={<Radio value={item.value} sx={{ display: 'none' }} />}
              sx={{ display: 'flex' }}
              slotProps={{ typography: { sx: { flex: 1 } } }}
              label={
                <MainCard
                  content={false}
                  border={false}
                  boxShadow={currentFontSize === item.value}
                  sx={(theme: Theme) => ({
                    bgcolor: 'secondary.lighter',
                    p: 1,
                    ...(currentFontSize === item.value && { bgcolor: 'primary.lighter', boxShadow: theme.vars.customShadows.primary })
                  })}
                >
                  <Box sx={{ minWidth: HEADER_HEIGHT, bgcolor: 'background.paper', p: 1, '&:hover': { bgcolor: 'background.paper' } }}>
                    <Stack sx={{ gap: 0.5, alignItems: 'center', justifyContent: 'flex-end', minHeight: 44 }}>
                      <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: 16 * FONT_SIZE_SCALE[item.value] }}>
                        Aa
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {item.label}
                      </Typography>
                    </Stack>
                  </Box>
                </MainCard>
              }
            />
          </Grid>
        ))}
      </Grid>
    </RadioGroup>
  );
}
